
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { ArgsTable, Canvas, Meta, Story } from '@storybook/addon-docs';
import { kebabCase } from 'lodash';
import tokens from '../../compiled/tokens/js/tokens.js';
import alignmentDemo from './demo/alignment.twig';
import colorDemo from './demo/color.twig';
import fontSizeDemo from './demo/font-size.twig';
const baseColorTokenKeys = Object.keys(tokens.color.base).map(kebabCase);
const colorControlConfig = {
  options: ['', ...baseColorTokenKeys],
  type: { name: 'string' },
  control: { type: 'select' },
};
const fontSizeControlConfig = {
  type: { name: 'string' },
  control: {
    type: 'select',
    options: [
      '',
      'big',
      'small',
      'heading-n-2',
      'heading-n-1',
      'heading-0',
      'heading-1',
      'heading-2',
      'heading-3',
    ],
  },
};


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Vendor/WordPress/Utilities" mdxType="Meta" />
    <h1>{`WordPress Block Editor Utilities`}</h1>
    <p>{`The WordPress `}<a parentName="p" {...{
        "href": "https://wordpress.org/gutenberg/"
      }}>{`Gutenberg editor`}</a>{` supports certain utility classes that may apply to blocks or to default markup.`}</p>
    <h2>{`Block Color Palettes`}</h2>
    <p>{`We include `}<inlineCode parentName="p">{`has-{color}-color`}</inlineCode>{` and `}<inlineCode parentName="p">{`has-{color}-background-color`}</inlineCode>{` classes for each of our base `}<a parentName="p" {...{
        "href": "/docs/design-tokens-color--page"
      }}>{`color tokens`}</a>{` to support `}<a parentName="p" {...{
        "href": "https://developer.wordpress.org/block-editor/how-to-guides/themes/theme-support/#block-color-palettes"
      }}>{`the `}<inlineCode parentName="a">{`editor-color-palette`}</inlineCode>{` feature`}</a>{`.`}</p>
    <p>{`Because WordPress does not differentiate between text and background colors as part of configuration, we don't restrict combinations within our CSS. It's critical that editors take note of WordPress's built-in warnings when inaccessible combinations are chosen.`}</p>
    <p>{`When a background color is chosen, rounded corners and padding may be added (depending on alignment) to more gracefully offset the container from its surrounding content.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Color" parameters={{
        layout: 'fullscreen'
      }} argTypes={{
        color: colorControlConfig,
        background_color: colorControlConfig
      }} args={{
        color: 'fuchsia-lighter',
        background_color: 'purple-darker'
      }} mdxType="Story">
    {args => colorDemo(args)}
  </Story>
    </Canvas>
    <ArgsTable story="Color" mdxType="ArgsTable" />
    <h2>{`Font Size`}</h2>
    <p>{`We include `}<inlineCode parentName="p">{`has-{font-size}-font-size`}</inlineCode>{` classes for our `}<inlineCode parentName="p">{`big`}</inlineCode>{`, `}<inlineCode parentName="p">{`small`}</inlineCode>{` and heading level `}<a parentName="p" {...{
        "href": "/docs/design-tokens-size--page"
      }}>{`size tokens`}</a>{` to support `}<a parentName="p" {...{
        "href": "https://developer.wordpress.org/block-editor/how-to-guides/themes/theme-support/#block-font-sizes"
      }}>{`the `}<inlineCode parentName="a">{`editor-font-sizes`}</inlineCode>{` feature`}</a>{`. Note that negative number class segments include an extra dash (`}<inlineCode parentName="p">{`n-1`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`n1`}</inlineCode>{`) due to WordPress's kebab-case formatting of slugs.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Font Size" argTypes={{
        font_size: fontSizeControlConfig
      }} args={{
        font_size: 'big'
      }} mdxType="Story">
    {args => fontSizeDemo(args)}
  </Story>
    </Canvas>
    <ArgsTable story="Font Size" mdxType="ArgsTable" />
    <h2>{`Alignment`}</h2>
    <p>{`By default, blocks with backgrounds and image blocks will attempt to fill the available inline padding of `}<a parentName="p" {...{
        "href": "/docs/objects-container--basic"
      }}>{`our containers`}</a>{`. You can opt into different alignment options for certain blocks.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`WordPress Class`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Behavior`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Blocks`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`(None)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fill available inline padding`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Code, Image, Video, blocks with background colors`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`aligncenter`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Stay within the container (do not fill)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Image, Video, blocks with background colors`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignleft`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Float left, constrain inline size`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Image`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignright`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Float right, constrain inline size`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Image`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignfull`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fill viewport inline size`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Blocks with alignment options`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`alignwide`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Fill viewport inline size up to a maximum`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Blocks with alignment options`}</td>
        </tr>
      </tbody>
    </table>
    <Canvas mdxType="Canvas">
  <Story name="Alignment" height="320px" parameters={{
        layout: 'fullscreen',
        docs: {
          inlineStories: false
        }
      }} argTypes={{
        alignment: {
          options: ['', 'alignleft', 'aligncenter', 'alignright', 'alignfull', 'alignwide'],
          control: {
            type: 'select'
          }
        }
      }} args={{
        alignment: 'alignwide'
      }} mdxType="Story">
    {args => alignmentDemo(args)}
  </Story>
    </Canvas>
    <p>{`(Note: Storybook's `}<inlineCode parentName="p">{`ArgsTable`}</inlineCode>{` does not seem to work with iframe stories at the time of this writing. To try out the different alignment options, `}<a parentName="p" {...{
        "href": "/story/vendor-wordpress-utilities--alignment"
      }}>{`view this story in "Canvas" mode`}</a>{`.)`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;

export const color = (args => colorDemo(args));
color.storyName = 'Color';
color.argTypes = {
  color: colorControlConfig,
  background_color: colorControlConfig
};
color.args = {
  color: 'fuchsia-lighter',
  background_color: 'purple-darker'
};
color.parameters = { storySource: { source: 'args => colorDemo(args)' }, ...{
  layout: 'fullscreen'
} };

export const fontSize = (args => fontSizeDemo(args));
fontSize.storyName = 'Font Size';
fontSize.argTypes = {
  font_size: fontSizeControlConfig
};
fontSize.args = {
  font_size: 'big'
};
fontSize.parameters = { storySource: { source: 'args => fontSizeDemo(args)' } };

export const alignment = (args => alignmentDemo(args));
alignment.storyName = 'Alignment';
alignment.argTypes = {
  alignment: {
    options: ['', 'alignleft', 'aligncenter', 'alignright', 'alignfull', 'alignwide'],
    control: {
      type: 'select'
    }
  }
};
alignment.args = {
  alignment: 'alignwide'
};
alignment.parameters = { storySource: { source: 'args => alignmentDemo(args)' }, ...{
  layout: 'fullscreen',
  docs: {
    inlineStories: false
  }
} };

const componentMeta = { title: 'Vendor/WordPress/Utilities', includeStories: ["color","fontSize","alignment"],  };

const mdxStoryNameToKey = {"Color":"color","Font Size":"fontSize","Alignment":"alignment"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;